import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";
import { 
  collection, query, where, onSnapshot, doc, addDoc, deleteDoc, updateDoc, getDocs 
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  Package, Search, Truck, MapPin, Plus, Trash2, Edit3, Layers, 
  ArrowRightLeft, Calendar, DollarSign, PieChart, RefreshCw, X, 
  Filter, TrendingUp, ClipboardEdit, History, Check, Eye, Download, Printer, FileText, UserPlus, Globe
} from "lucide-react";

// --- BARCODE PRINT MODAL ---
const BarcodePrintModal = ({ isOpen, onClose, productsToPrint, storeName }) => {
  const [quantities, setQuantities] = useState({});

  useEffect(() => {
    if (isOpen && productsToPrint?.length > 0) {
      const initialQtys = {};
      productsToPrint.forEach(p => {
        initialQtys[p.id] = 1;
      });
      setQuantities(initialQtys);
    }
  }, [isOpen, productsToPrint]);

  if (!isOpen || !productsToPrint || productsToPrint.length === 0) return null;

  const handleQuantityChange = (id, val) => {
    setQuantities(prev => ({ ...prev, [id]: Number(val) }));
  };

const handlePrint = () => {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  let labelsHtml = '';
  
  for (const product of productsToPrint) {
    const printQty = quantities[product.id] || 1;
    const safeBarcode = String(product.productCode || "UNKNOWN").toUpperCase().replace(/\s+/g, '');

    for(let i = 0; i < printQty; i++) {
       labelsHtml += `
         <div style="
           width: 3.93in; 
           height: 0.54in; 
           box-sizing: border-box; 
           padding-top: 5mm; 
           padding-left: 4mm; 
           display: flex; 
           flex-direction: row; 
           justify-content: flex-start; /* This forces everything to the left side */
           align-items: center; 
           overflow: hidden; 
           background: #fff; 
           page-break-after: always;
           break-after: page;
           page-break-inside: avoid;
           color: #000;
         ">
           
           <div style="
             width: 30%; 
             box-sizing: border-box;
             display: flex; 
             flex-direction: column; 
             justify-content: space-between; 
             align-items: flex-start; 
             height: 100%; 
             text-align: left; 
             border-right: 1px dashed #ccc; /* Keeps the middle fold line */
             padding-right: 1mm;
           ">
             <div style="font-family: 'Segoe UI', sans-serif; font-size: 10px; font-weight: 900; text-transform: uppercase; white-space: nowrap; line-height: 1;">
               ${product.productName || product.details || "ITEM"}
             </div>
             <div style="font-family: 'Segoe UI', sans-serif; font-size: 12px; font-weight: 900; line-height: 1.2;">
               Rs ${product.retailPrice || 0}
             </div>
             <div style="font-family: 'Segoe UI', sans-serif; font-size: 9px; font-weight: 800; line-height: 1;">
               ${safeBarcode}
             </div>
           </div>
           
           <div style="
             width: 30%; 
             box-sizing: border-box;
             display: flex; 
             flex-direction: column; 
             align-items: flex-start; 
             justify-content: space-between; 
             height: 100%; 
             text-align: left; 
             padding-left: 1mm;
           ">
             <div style="font-family: 'Segoe UI', sans-serif; font-size: 8px; font-weight: 800; text-align: left; white-space: nowrap; line-height: 1;">
               Shruti Eye Care
             </div>
             
             <div style="font-family: 'Libre Barcode 39', monospace; font-size: 24px; line-height: 0.7; overflow: hidden; margin-top: 1mm;">
               *${safeBarcode}*
             </div>
             
             <div style="font-family: 'Segoe UI', sans-serif; font-size: 10px; font-weight: 900; letter-spacing: 1px; line-height: 1; margin-top: 1mm;">
               ${safeBarcode}
             </div>
           </div>
           
           </div>
       `;
    }
  }

  const htmlContent = `
    <html>
      <head>
        <title>Print Barcodes</title>
        <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+39&display=swap" rel="stylesheet">
        <style>
          @page { 
            size: 3.93in 0.54in; 
            margin: 0 !important;
          }
          body { 
            margin: 0; 
            padding: 0; 
            background: #fff; 
          }
        </style>
      </head>
      <body>
        ${labelsHtml}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 500);
          };
        </script>
      </body>
    </html>
  `;
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  onClose();
};

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white p-6 rounded-3xl w-full max-w-md shadow-2xl">
        <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
          <h3 className="text-sm font-black uppercase text-slate-800 flex items-center gap-2">
            <Printer size={16} className="text-blue-600"/> Print Bulk Barcodes
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-rose-500 transition-colors"><X size={16} /></button>
        </div>
        
        <div className="space-y-4">
          <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">
            Specify quantities per product
          </label>
          <div className="max-h-[40vh] overflow-y-auto space-y-2 pr-2">
            {productsToPrint.map(product => {
              const safeCode = String(product.productCode || "").toUpperCase().replace(/\s+/g, '');
              return (
                <div key={product.id} className="bg-slate-50 p-3 rounded-xl border border-slate-200 flex items-center justify-between gap-4">
                  <div className="text-xs overflow-hidden flex-1">
                    <div className="font-bold text-slate-800 truncate">{product.productName || product.details || "Unnamed Product"}</div>
                    <div className="font-mono text-blue-600 font-black mt-0.5">{safeCode}</div>
                  </div>
                  <div className="w-24">
                    <input 
                      type="number" min="1" 
                      value={quantities[product.id] || 1} 
                      onChange={e => handleQuantityChange(product.id, e.target.value)} 
                      className="w-full p-2 bg-white border border-slate-200 rounded-lg outline-none font-mono font-black focus:border-blue-500 transition-colors text-center" 
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 font-bold text-xs transition-colors">Cancel</button>
          <button onClick={handlePrint} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-widest text-xs rounded-xl shadow-md shadow-blue-200 transition-colors flex justify-center items-center gap-2">
            <Printer size={14}/> Print All
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// --- DYNAMIC PRODUCT MODAL COMPONENT ---
const ProductEntryModal = ({ isOpen, onClose, type, onAdd, isDirectInventory }) => {
  const [formData, setFormData] = useState({
    trackInventory: "Yes", allowNegative: "No", barcodeOption: "System Generated / Unique", quantity: 1, considerAsPair: "No"
  });

  if (!isOpen) return null;
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const calculateTotal = () => {
    const qty = Number(formData.quantity) || 0;
    let price = 0;
    if (type === "Contact Lens") {
      price = Number(formData.purchaseRsPerBox) || 0;
      const boxes = Number(formData.noOfBoxes) || 0;
      return price * boxes;
    } else {
      price = Number(formData.purchaseRs) || 0;
      return price * qty;
    }
  };

  const renderSpecificFields = () => {
    if (type === "Frame" || type === "Sunglasses") {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs">
          <div><label className="block text-slate-500 font-bold mb-1">Product Code</label><input name="productCode" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div className="col-span-2"><label className="block text-slate-500 font-bold mb-1">*Name</label><input name="productName" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Brand</label><input name="brand" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Gender</label><input name="gender" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Color</label><input name="color" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Size</label><input name="size" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Type</label><input name="typeDetail" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Shape</label><input name="shape" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Material</label><input name="material" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Temple Detail</label><input name="templeDetail" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Bridge Size</label><input name="bridgeSize" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Quality</label><input name="quality" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">*Quantity</label><input type="number" name="quantity" defaultValue={1} onChange={handleChange} className="w-full p-2 border rounded" /></div>
        </div>
      );
    }
    
    if (type === "Lens") {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs">
          <div><label className="block text-slate-500 font-bold mb-1">Product Code</label><input name="productCode" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div className="col-span-2"><label className="block text-slate-500 font-bold mb-1">Name</label><input name="productName" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Brand</label><input name="brand" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Color</label><input name="color" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Material</label><input name="material" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Coating</label><input name="coating" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Design</label><input name="design" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Index</label><input name="index" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Quality</label><input name="quality" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">SPH</label><input name="sph" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">CYL</label><input name="cyl" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Addition</label><input name="addition" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Axis</label><input name="axis" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">*Quantity</label><input type="number" name="quantity" defaultValue={1} onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Consider As Pair</label><select name="considerAsPair" onChange={handleChange} className="w-full p-2 border rounded"><option>Yes</option><option>No</option></select></div>
        </div>
      );
    }

    if (type === "Contact Lens") {
      return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs">
          <div><label className="block text-slate-500 font-bold mb-1">Product Code</label><input name="productCode" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div className="col-span-2"><label className="block text-slate-500 font-bold mb-1">*Product Name</label><input name="productName" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Brand</label><input name="brand" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Color</label><input name="color" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Number</label><input name="number" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">CT (Thickness)</label><input name="ct" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Type</label><input name="typeDetail" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Materials</label><input name="materials" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Modality</label><input name="modality" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Validity In Days</label><input type="number" name="validityInDays" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">WC (Water Content)</label><input name="wc" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Dk/t (Permeability)</label><input name="dkt" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Quality</label><input name="quality" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">SPH</label><input name="sph" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">CYL</label><input name="cyl" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Addition</label><input name="addition" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Axis</label><input name="axis" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Base Curves (BC)</label><input name="bc" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Diameter (DIA)</label><input name="dia" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Power Type</label><input name="powerType" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Batch Number</label><input name="batchNumber" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Mfg Date</label><input type="date" name="mfgDate" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Expiry Date</label><input type="date" name="expiryDate" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">No of Boxes</label><input type="number" name="noOfBoxes" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">Pieces Per Box</label><input type="number" name="piecesPerBox" onChange={handleChange} className="w-full p-2 border rounded" /></div>
          <div><label className="block text-slate-500 font-bold mb-1">*Quantity (Auto)</label><input type="number" name="quantity" onChange={handleChange} className="w-full p-2 border rounded bg-slate-50" readOnly value={(formData.noOfBoxes || 0) * (formData.piecesPerBox || 0)} /></div>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-xs">
        <div><label className="block text-slate-500 font-bold mb-1">Product Code</label><input name="productCode" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div className="col-span-2"><label className="block text-slate-500 font-bold mb-1">*Product Name</label><input name="productName" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">Brand</label><input name="brand" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">Variant</label><input name="variant" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">Packing Type</label><input name="packingType" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">Color</label><input name="color" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">Quality</label><input name="quality" onChange={handleChange} className="w-full p-2 border rounded" /></div>
        <div><label className="block text-slate-500 font-bold mb-1">*Quantity</label><input type="number" name="quantity" defaultValue={1} onChange={handleChange} className="w-full p-2 border rounded" /></div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="w-full max-w-5xl bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-4 shadow-2xl my-8">
        <div className="flex justify-between items-center border-b border-slate-100 pb-4">
          <h3 className="text-lg font-black uppercase tracking-wider text-slate-800 flex items-center gap-2">
            <Package size={20} className="text-blue-600"/> Add {type} {isDirectInventory ? "to Inventory" : "to Purchase"}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-rose-500 bg-slate-50 p-2 rounded-full transition-colors"><X size={16} /></button>
        </div>

        {renderSpecificFields()}

        <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-4 text-xs">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {type === "Contact Lens" ? (
              <>
                <div><label className="block text-slate-500 font-bold mb-1">*Purchase Rs Per Box</label><input type="number" name="purchaseRsPerBox" onChange={handleChange} className="w-full p-2 border rounded font-mono" /></div>
                <div><label className="block text-slate-500 font-bold mb-1">Retail Price Per Box</label><input type="number" name="retailPricePerBox" onChange={handleChange} className="w-full p-2 border rounded font-mono" /></div>
                <div><label className="block text-slate-500 font-bold mb-1">Total Purchase Amount</label><input type="text" value={calculateTotal()} readOnly className="w-full p-2 border rounded bg-slate-100 font-mono font-black text-emerald-600" /></div>
              </>
            ) : (
              <>
                <div><label className="block text-slate-500 font-bold mb-1">*Purchase Rs</label><input type="number" name="purchaseRs" onChange={handleChange} className="w-full p-2 border rounded font-mono" /></div>
                <div><label className="block text-slate-500 font-bold mb-1">Retail Price</label><input type="number" name="retailPrice" onChange={handleChange} className="w-full p-2 border rounded font-mono" /></div>
                <div><label className="block text-slate-500 font-bold mb-1">Basic Price</label><input type="number" name="basicPrice" onChange={handleChange} className="w-full p-2 border rounded font-mono" /></div>
                <div><label className="block text-slate-500 font-bold mb-1">Total Purchase Amount</label><input type="text" value={calculateTotal()} readOnly className="w-full p-2 border rounded bg-slate-100 font-mono font-black text-emerald-600" /></div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
          <button onClick={onClose} className="px-6 py-2.5 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 font-bold transition-colors">Cancel</button>
          <button onClick={() => { onAdd({ ...formData, productType: type, totalPurchaseAmount: calculateTotal() }); onClose(); }} className="px-8 py-2.5 bg-blue-600 rounded-xl hover:bg-blue-700 text-white font-black uppercase tracking-widest shadow-md shadow-blue-200 transition-colors">
            {isDirectInventory ? "Save to Inventory" : "Add to Purchase"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default function Inventory() {
  const [activeTab, setActiveTab] = useState("inventory"); 
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Single Tenant Auth states
  const [activeBranchId, setActiveBranchId] = useState(""); 
  const [storeName, setStoreName] = useState("");

  const [storeId, setStoreId] = useState("");
  const [storesList, setStoresList] = useState([]);

  const [search, setSearch] = useState("");
  const [barcodeModal, setBarcodeModal] = useState({ open: false, productsToPrint: [] });
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [transferLogs, setTransferLogs] = useState([]);
  const [editLogs, setEditLogs] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // States for Global Inventory
  const [globalInventory, setGlobalInventory] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");

  // Added States for Add Vendor Modal
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [vendorForm, setVendorForm] = useState({ name: "", contactNo: "", gstin: "", address: "" });

  const [purchaseHeader, setPurchaseHeader] = useState({ supplier: "", billNo: "", date: "", taxRule: "Not Applicable" });
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [productModal, setProductModal] = useState({ open: false, type: "", isDirectInventory: false });
  
  // Updated Transfer States
  const [transferForm, setTransferForm] = useState({ toBranchId: "", remarks: "" });
  const [transferSearch, setTransferSearch] = useState("");
  const [transferCart, setTransferCart] = useState([]); // [{ product, transferQty }]
  const [showTransferHistory, setShowTransferHistory] = useState(false);

  // --- NEW STATES FOR FILTERING AND MULTI-SELECT ---
  const productTypes = ["Frame", "Lens", "Sunglasses", "Contact Lens", "Solution", "Other", "Non Chargeable"];
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  // 1. Authenticate and find Current Logged In Store
  useEffect(() => {
    const auth = getAuth();
    let unsubStore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        const storeQuery = query(collection(db, "stores"), where("linkedAuthUid", "==", user.uid));
        
        unsubStore = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0];
            setActiveBranchId(storeDoc.id);
            setStoreName(storeDoc.data().storeName || storeDoc.data().storeId);
            setStoreId(storeDoc.data().storeId);
          } else {
            console.warn("No authorized store found for this user.");
          }
        });
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubStore) unsubStore();
    };
  }, []);

  // 2. Fetch ALL stores to populate the Stock Transfer dropdown & Global Inventory
  useEffect(() => {
    const unsubStores = onSnapshot(collection(db, "stores"), (snapshot) => {
      const pulledStores = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setStoresList(pulledStores);
    });
    return () => unsubStores();
  }, []);

  // 3. Attach Real-Time Listeners to the CURRENT STORE's Inventory
  useEffect(() => {
    if (!activeBranchId) return;

    const unsubProducts = onSnapshot(collection(db, "stores", activeBranchId, "inventory"), (snapshot) => {
      setProducts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubPurchases = onSnapshot(collection(db, "stores", activeBranchId, "purchases"), (snapshot) => {
      setPurchaseHistory(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)));
    });
    const unsubSuppliers = onSnapshot(collection(db, "inventory_suppliers"), (snapshot) => {
      setSuppliers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubTransfers = onSnapshot(collection(db, "transfer_history"), (snapshot) => {
      setTransferLogs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => new Date(b.transferredAt || 0) - new Date(a.transferredAt || 0)));
    });
    const unsubEditLogs = onSnapshot(collection(db, "stores", activeBranchId, "stock_edit_logs"), (snapshot) => {
      setEditLogs(snapshot.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)));
    });

    return () => { unsubProducts(); unsubPurchases(); unsubSuppliers(); unsubTransfers(); unsubEditLogs(); };
  }, [activeBranchId]);
  
  // 4. Fetch Global Inventory across all stores AND Super Admin
  useEffect(() => {
    if (activeTab === "globalInventory" && storesList.length > 0) {
      setIsProcessing(true);
      const fetchAllInventories = async () => {
        try {
          let allItems = [];
          
          // Fetch from all regular branches
          for (const store of storesList) {
            const sName = store.storeName || store.storeId || store.id;
            const invRef = collection(db, "stores", store.id, "inventory");
            const snapshot = await getDocs(invRef);
            snapshot.forEach(doc => {
              allItems.push({
                id: doc.id,
                storeId: store.id,
                storeName: sName,
                ...doc.data()
              });
            });
          }

          // Fetch from SUPER ADMIN WAREHOUSE
          try {
            const superAdminRef = collection(db, "stores", "SUPER_ADMIN_WAREHOUSE", "inventory");
            const superAdminSnap = await getDocs(superAdminRef);
            superAdminSnap.forEach(doc => {
              allItems.push({
                id: doc.id,
                storeId: "SUPER_ADMIN_WAREHOUSE",
                storeName: "Super Admin Warehouse / HQ",
                ...doc.data()
              });
            });
          } catch (e) {
             console.error("Super Admin inventory fetch failed:", e);
          }

          setGlobalInventory(allItems);
        } catch (error) {
          console.error("Error fetching global inventory:", error);
        } finally {
          setIsProcessing(false);
        }
      };
      fetchAllInventories();
    }
  }, [activeTab, storesList]);

  // --- FILTER & SEARCH PROCESSING ---
  const processedProducts = useMemo(() => {
    return products.filter(p => {
      const q = (search || "").toLowerCase();
      const matchesSearch = !search.trim() || 
        String(p.productName || "").toLowerCase().includes(q) || 
        String(p.details || "").toLowerCase().includes(q) || 
        String(p.productCode || "").toLowerCase().includes(q);
      
      const matchesCategory = selectedCategory === "All" || p.productType === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [products, search, selectedCategory]);

  const isAllSelected = processedProducts.length > 0 && processedProducts.every(p => selectedRowIds.includes(p.id));
  
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRowIds([]);
    } else {
      setSelectedRowIds(processedProducts.map(p => p.id));
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRowIds(prev => prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]);
  };


  const handleAddInvoiceItem = (itemData) => setInvoiceItems([...invoiceItems, itemData]);

  const handleAddDirectInventory = async (itemData) => {
    setIsProcessing(true);
    try {
      const matchItem = products.find(p => p.productCode?.toLowerCase() === itemData.productCode?.trim().toLowerCase());
      const parsedQty = Number(itemData.quantity) || (Number(itemData.noOfBoxes) * Number(itemData.piecesPerBox)) || 1;
      const purchasePrice = Number(itemData.purchaseRs) || Number(itemData.purchaseRsPerBox) || 0;
      const retailPrice = Number(itemData.retailPrice) || Number(itemData.retailPricePerBox) || 0;

      const recordPayload = { ...itemData, quantity: parsedQty, purchasePrice, retailPrice, purchaseDate: new Date().toISOString().split("T")[0] };

      if (matchItem) {
        await updateDoc(doc(db, "stores", activeBranchId, "inventory", matchItem.id), { ...recordPayload, quantity: Number(matchItem.quantity) + parsedQty, updatedAt: new Date().toISOString() });
      } else {
        await addDoc(collection(db, "stores", activeBranchId, "inventory"), { ...recordPayload, createdAt: new Date().toISOString() });
      }
      alert("Inventory successfully updated.");
    } catch (err) { alert("Error adding inventory."); } finally { setIsProcessing(false); }
  };

  const submitPurchaseInvoice = async () => {
    if (invoiceItems.length === 0 || !purchaseHeader.supplier || !purchaseHeader.billNo) {
      alert("Please ensure Supplier Name, Bill Number are filled and at least one item is added."); return;
    }
    setIsProcessing(true);
    try {
      const totalInvoiceQty = invoiceItems.reduce((acc, it) => acc + (Number(it.quantity) || (Number(it.noOfBoxes)*Number(it.piecesPerBox)) || 1), 0);
      const totalInvoiceAmount = invoiceItems.reduce((acc, it) => acc + (Number(it.totalPurchaseAmount) || 0), 0);

      const invoicePayload = { supplier: purchaseHeader.supplier, billNo: purchaseHeader.billNo, taxRule: purchaseHeader.taxRule, purchaseDate: purchaseHeader.date || new Date().toISOString().split("T")[0], totalQuantity: totalInvoiceQty, totalAmount: totalInvoiceAmount, items: invoiceItems, createdAt: new Date().toISOString() };
      
      await addDoc(collection(db, "stores", activeBranchId, "purchases"), invoicePayload);

      for (const item of invoiceItems) {
        const matchItem = products.find(p => p.productCode?.toLowerCase() === item.productCode?.trim().toLowerCase());
        const parsedQty = Number(item.quantity) || (Number(item.noOfBoxes)*Number(item.piecesPerBox)) || 1;
        const purchasePrice = Number(item.purchaseRs) || Number(item.purchaseRsPerBox) || 0;
        const retailPrice = Number(item.retailPrice) || Number(item.retailPricePerBox) || 0;
        const recordPayload = { ...item, supplier: purchaseHeader.supplier, billNo: purchaseHeader.billNo, purchaseDate: purchaseHeader.date || new Date().toISOString().split("T")[0], quantity: parsedQty, purchasePrice, retailPrice };

        if (matchItem) {
          await updateDoc(doc(db, "stores", activeBranchId, "inventory", matchItem.id), { ...recordPayload, quantity: Number(matchItem.quantity) + parsedQty, updatedAt: new Date().toISOString() });
        } else {
          await addDoc(collection(db, "stores", activeBranchId, "inventory"), { ...recordPayload, createdAt: new Date().toISOString() });
        }
      }
      setInvoiceItems([]); setPurchaseHeader({ supplier: "", billNo: "", date: "", taxRule: "Not Applicable" });
      alert("Purchase Invoice Saved.");
    } catch (err) { alert("Error saving purchase records."); } finally { setIsProcessing(false); }
  };

  const submitVendor = async (e) => {
    e.preventDefault();
    if (!vendorForm.name) return alert("Vendor name is required.");
    setIsProcessing(true);
    try {
      await addDoc(collection(db, "inventory_suppliers"), vendorForm);
      setIsVendorModalOpen(false);
      setVendorForm({ name: "", contactNo: "", gstin: "", address: "" });
      alert("Supplier/Vendor successfully added.");
    } catch (error) {
      alert("Error saving vendor");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditInventorySave = async (updatedProduct) => {
    try {
      const original = products.find(p => p.id === updatedProduct.id);
      
      await addDoc(collection(db, "stores", activeBranchId, "stock_edit_logs"), {
        productId: updatedProduct.id,
        productCode: updatedProduct.productCode || "",
        productName: updatedProduct.productName || updatedProduct.details || "",
        oldQuantity: original.quantity || 0,
        newQuantity: updatedProduct.quantity || 0,
        oldPrice: original.retailPrice || 0,
        newPrice: updatedProduct.retailPrice || 0,
        timestamp: new Date().toISOString()
      });

      await updateDoc(doc(db, "stores", activeBranchId, "inventory", updatedProduct.id), updatedProduct);
      setEditingProduct(null);
      alert("Inventory updated and logged successfully.");
    } catch (error) {
      console.error(error);
      alert("Error saving edits.");
    }
  };

  const handleEditChange = (e) => {
    setEditingProduct({ ...editingProduct, [e.target.name]: e.target.value });
  };

  const executeStockTransfer = async () => {
    if (!transferForm.toBranchId || activeBranchId === transferForm.toBranchId) { 
      alert("Please select a valid destination branch."); 
      return; 
    }
    if (transferCart.length === 0) {
      alert("No items selected for transfer.");
      return;
    }

    const currentActiveBranchName = storeName || activeBranchId;
    const toStore = storesList.find(s => s.id === transferForm.toBranchId);
    const toBranchName = toStore?.storeId || transferForm.toBranchId;

    try {
      // Loop through transferCart
      for (const item of transferCart) {
        const qty = Number(item.transferQty);
        if (qty <= 0 || qty > item.product.quantity) continue; // Skip invalid

        // Deduct from current inventory
        await updateDoc(doc(db, "stores", activeBranchId, "inventory", item.product.id), { 
            quantity: Number(item.product.quantity) - qty 
        });
        
        const { id, ...itemToTransfer } = item.product;

        // Add to target inventory
        await addDoc(collection(db, "stores", transferForm.toBranchId, "inventory"), { 
            ...itemToTransfer, 
            quantity: qty, 
            transferRemark: `Stock Transfer from branch [${currentActiveBranchName}]`, 
            transferNotes: transferForm.remarks, 
            purchaseDate: new Date().toISOString().split("T")[0], 
            createdAt: new Date().toISOString() 
        });
        
        // Add to history
        await addDoc(collection(db, "transfer_history"), { 
            productCode: String(item.product.productCode || "").toUpperCase(), 
            productName: item.product.productName || item.product.details || "", 
            quantity: qty, 
            fromBranch: currentActiveBranchName, 
            toBranch: toBranchName, 
            transferredAt: new Date().toISOString() 
        });
      }

      setTransferCart([]); // Clear Staging Cart
      setTransferForm({ ...transferForm, remarks: "" }); 
      alert("Multiple stock transfers executed successfully.");
    } catch (err) { 
      console.error(err); 
      alert("Error executing transfers.");
    }
  };

  const handleDeleteTransfer = async (transferId) => {
    if (!window.confirm("Are you sure you want to delete this transfer log?")) return;
    try { 
      await deleteDoc(doc(db, "transfer_history", transferId)); 
    } catch (err) { 
      console.error(err); 
      alert("Failed to delete log.");
    }
  };

  const handlePurgeProduct = async (productId) => {
    if (!window.confirm("Remove this entry entirely?")) return;
    try { await deleteDoc(doc(db, "stores", activeBranchId, "inventory", productId)); } catch (err) { console.error(err); }
  };

  const printPurchaseBill = (record) => {
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
      <meta charset="UTF-8">
      <title>Purchase Invoice - ${record.billNo}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; }
        @page { size: A4; margin: 15mm; padding: 10mm; }
        body { background: #fff; color: #222; -webkit-print-color-adjust: exact; }
        .a4-page { width: 100%; max-width: 210mm; margin: 0 auto; background: white; padding: 20px; }
        
        .logo img { max-width: 80px; height: auto; margin-bottom: 5px; }

        .header { text-align: center; border-bottom: 2px solid #16205f; padding-bottom: 15px; margin-bottom: 20px; }
        .clinic-name { font-size: 26px; font-weight: 900; color: #16205f; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
        .store-address { font-size: 12px; color: #555; margin-bottom: 8px; }
        .title { display: inline-block; font-size: 18px; font-weight: 700; color: #fff; background: #16205f; padding: 6px 20px; border-radius: 20px; text-transform: uppercase; }
        
        .patient-section { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px; }
        .info-box { border: 1px solid #e4e4e4; border-radius: 8px; padding: 15px; flex: 1; background: #fafafa; }
        .info-box h4 { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
        
        .section { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 25px; overflow: hidden; }
        .section-title { background: #f0f4f8; padding: 10px 15px; font-size: 14px; font-weight: bold; border-bottom: 1px solid #ddd; color: #16205f; text-transform: uppercase; }
        
        table { width: 100%; border-collapse: collapse; }
        th { background: #fafafa; font-size: 11px; padding: 10px; border: 1px solid #ddd; text-transform: uppercase; color: #444; }
        td { text-align: center; padding: 10px; border: 1px solid #ddd; font-size: 13px; font-weight: 600; color: #111; }
        
        .footer-reminder { display: flex; justify-content: space-between; align-items: center; border: 1px solid #ddd; border-radius: 8px; padding: 15px 20px; background: #f8f9fa; margin-bottom: 30px; }
        .footer-reminder .text { font-size: 13px; color: #555; text-transform: uppercase; font-weight: bold; }
        .footer-reminder .date { font-size: 20px; font-weight: 900; color: #16205f; }
        .footer { text-align: center; margin-top: 40px; color: #888; font-size: 10px; border-top: 1px solid #eee; padding-top: 10px; }
      </style>
      </head>
      <body>
        <div class="a4-page">
          <div class="header">
            <div class="logo"><img src="${window.location.origin}/logo192.png" alt="Logo" /></div>

            <div class="clinic-name">Shruti Eye Care & Opticals</div>
            <div class="store-address">CHANDRAKANTI COMPLEX OPP KHAJPURA, PATNA - 800014, Bihar, India</div>
            <div class="title">Purchase Bill Report</div>
          </div>

          <div class="patient-section">
            <div class="info-box">
              <h4>Supplier Details</h4>
              <div class="info-row"><span>Vendor Name:</span> <strong>${record.supplier}</strong></div>
              <div class="info-row"><span>Bill No:</span> <strong>${record.billNo}</strong></div>
            </div>
            <div class="info-box">
              <h4>Invoice Metadata</h4>
              <div class="info-row"><span>Date:</span> <strong>${new Date(record.createdAt).toLocaleDateString()}</strong></div>
              <div class="info-row"><span>Tax Rule:</span> <strong>${record.taxRule || 'N/A'}</strong></div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Purchased Items Matrix</div>
            <table>
              <tr>
                <th>S.No</th><th>Code</th><th>Product Name</th><th>Qty</th><th>Cost Price</th><th>Total</th>
              </tr>
              ${record.items.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.productCode}</td>
                  <td>${item.productName || item.details}</td>
                  <td>${item.quantity || (item.noOfBoxes*item.piecesPerBox) || 1}</td>
                  <td>₹${item.purchaseRs || item.purchaseRsPerBox || 0}</td>
                  <td>₹${item.totalPurchaseAmount || 0}</td>
                </tr>
              `).join('')}
            </table>
          </div>

          <div class="footer-reminder">
            <div class="text">Total Invoice Amount:</div>
            <div class="date">₹${record.totalAmount?.toLocaleString()}</div>
          </div>

          <div class="footer">
            © Shruti Eye Care & Opticals | Generated via Inventory Management System
          </div>
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }, 300);
          };
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };
  

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans antialiased">
      <StoreSidebar />

      <div className="flex-1 p-4 lg:p-8 overflow-x-hidden space-y-6">
        
        {/* HEADER CONTROL CONSOLE */}
        <div className="flex flex-col md:flex-row md:items-center justify-between p-6 bg-white border border-slate-200 rounded-3xl gap-4 shadow-sm">
          <div>
            <h1 className="text-2xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-3">
              <Package className="text-blue-600" size={28} /> Stock & Inventory Management
            </h1>
            <div className="flex items-center gap-1.5 mt-1.5 text-sm text-slate-500 font-medium">
            <MapPin size={14} className="text-blue-500" />
            <span>Operating Branch: </span>
            <span className="bg-blue-50 text-blue-700 font-black px-3 py-1 rounded-xl border border-blue-100 text-xs tracking-widest uppercase shadow-sm">
              {storeId || "Loading..."}
            </span>
          </div>
          </div>
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-3.5 text-slate-400" size={16} />
            <input type="text" placeholder="Filter inventory..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 text-sm font-medium rounded-xl outline-none focus:bg-white focus:border-blue-500 transition-colors" />
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-white p-3 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex flex-wrap gap-1.5 text-xs font-bold w-full xl:w-auto">
            <button onClick={() => setActiveTab("purchase")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "purchase" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><FileText size={14} /> Add Purchase</button>
            <button onClick={() => setActiveTab("inventory")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "inventory" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><Package size={14} /> Add Inventory</button>
            <button onClick={() => setActiveTab("transfer")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "transfer" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><Truck size={14} /> Stock Transfer</button>
            <button onClick={() => setActiveTab("editLogs")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "editLogs" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><ClipboardEdit size={14} /> Edit Logs</button>
            <button onClick={() => setActiveTab("analyticsDashboard")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "analyticsDashboard" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><PieChart size={14} /> Reports</button>
            <button onClick={() => setActiveTab("globalInventory")} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all ${activeTab === "globalInventory" ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 text-slate-500 hover:text-slate-800"}`}><Globe size={14} /> Global Warehouse</button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }} transition={{ duration: 0.15 }}>
            
            {/* TAB 1: ADD PURCHASE INVOICE */}
            {activeTab === "purchase" && (
              <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                  <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                    <h2 className="text-base font-black uppercase text-slate-800 flex items-center gap-2"><FileText size={18} className="text-blue-600"/> Add Stock Purchase</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-xs font-medium">
                    <div>
                      <label className="block text-slate-500 font-bold mb-1.5 uppercase text-[10px]">*Supplier Name :</label>
                      <div className="flex gap-2">
                        <select value={purchaseHeader.supplier} onChange={e => setPurchaseHeader({ ...purchaseHeader, supplier: e.target.value })} className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500">
                          <option value="">Select Vendor</option>
                          {suppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        <button onClick={() => setIsVendorModalOpen(true)} className="px-3 bg-blue-50 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-600 hover:text-white transition-colors flex justify-center items-center" title="Add New Supplier">
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-500 font-bold mb-1.5 uppercase text-[10px]">*Bill Number :</label>
                      <input value={purchaseHeader.billNo} onChange={e => setPurchaseHeader({ ...purchaseHeader, billNo: e.target.value })} placeholder="Ex: INV-2026" className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-blue-500" />
                    </div>
                    <div>
                      <label className="block text-slate-500 font-bold mb-1.5 uppercase text-[10px]">*Date :</label>
                      <input type="date" value={purchaseHeader.date} onChange={e => setPurchaseHeader({ ...purchaseHeader, date: e.target.value })} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-mono focus:bg-white focus:border-blue-500" />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                  <div>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {productTypes.map(type => (
                        <button key={type} onClick={() => setProductModal({ open: true, type, isDirectInventory: false })} className="px-4 py-2 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-bold rounded-xl hover:bg-blue-600 hover:text-white transition-colors">
                          + Add {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="overflow-x-auto text-xs font-medium border border-slate-200 rounded-xl mt-6">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200 font-bold text-slate-500 uppercase text-[10px]">
                          <th className="p-3">#</th><th className="p-3">Code</th><th className="p-3">Details</th><th className="p-3">Unit Price</th><th className="p-3">Qty</th><th className="p-3">Total</th><th className="p-3 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoiceItems.map((item, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-3">{idx + 1}</td><td className="p-3">{item.productCode}</td><td className="p-3">{item.productName || item.details}</td><td className="p-3">{item.purchaseRs || item.purchaseRsPerBox}</td><td className="p-3">{item.quantity || (item.noOfBoxes * item.piecesPerBox)}</td><td className="p-3">{item.totalPurchaseAmount}</td>
                            <td className="p-3 text-center"><button onClick={() => setInvoiceItems(invoiceItems.filter((_, i) => i !== idx))} className="p-1.5 bg-rose-50 text-rose-500 rounded"><Trash2 size={14}/></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 text-right">
                    <button onClick={submitPurchaseInvoice} className="px-8 py-3 bg-emerald-600 text-white font-bold rounded-xl shadow">Save Purchase Invoice</button>
                  </div>
                </div>

                {/* PURCHASE HISTORY TABLE WITH PRINT */}
                <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm mt-8">
                  <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h3 className="text-sm font-black uppercase text-slate-800 flex items-center gap-2"><History size={16} className="text-blue-500" /> Purchase Invoice Logs</h3>
                  </div>
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-slate-50">
                      <tr><th className="p-4">#</th><th className="p-4">Date</th><th className="p-4">Supplier</th><th className="p-4">Bill No</th><th className="p-4">Amount</th><th className="p-4">Action</th></tr>
                    </thead>
                    <tbody>
                      {purchaseHistory.map((ph, idx) => (
                        <tr key={ph.id} className="border-b hover:bg-slate-50">
                          <td className="p-4 font-bold text-slate-500">{idx + 1}</td>
                          <td className="p-4">{new Date(ph.createdAt).toLocaleDateString()}</td><td className="p-4 font-bold">{ph.supplier}</td><td className="p-4 font-mono">{ph.billNo}</td><td className="p-4 text-emerald-600 font-bold">₹{ph.totalAmount}</td>
                          <td className="p-4">
                            <button onClick={() => printPurchaseBill(ph)} className="px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-600 hover:text-white font-bold rounded-lg flex items-center gap-1"><Printer size={12}/> Print Bill</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 2: INVENTORY & EDIT */}
            {activeTab === "inventory" && (
              <div className="space-y-6">
                
                {/* --- NEW: CATEGORY FILTERS --- */}
                <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {["All", ...productTypes].map(cat => (
                      <button 
                        key={cat} 
                        onClick={() => setSelectedCategory(cat)} 
                        className={`px-4 py-2 text-xs font-bold rounded-xl transition-colors ${selectedCategory === cat ? "bg-blue-600 text-white shadow-md shadow-blue-200" : "bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
                      >
                        {cat === "All" ? "All Categories" : cat}
                      </button>
                    ))}
                  </div>

                  {/* Multi-Print Button Appears when items are selected */}
                  {selectedRowIds.length > 0 && (
                    <button 
                      onClick={() => setBarcodeModal({ open: true, productsToPrint: products.filter(p => selectedRowIds.includes(p.id)) })}
                      className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest rounded-xl shadow-md shadow-indigo-200 transition-colors shrink-0"
                    >
                      <Printer size={14} /> Print {selectedRowIds.length} Selected
                    </button>
                  )}
                </div>

                <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm mt-8">
                  <div className="p-5 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <h3 className="text-sm font-black uppercase text-slate-800 flex items-center gap-2"><Layers size={16} className="text-blue-500" /> Current Stock Inventory</h3>
                  </div>
                  <div className="overflow-x-auto text-xs font-medium max-h-[600px]">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 bg-slate-50 shadow-sm z-10">
                        <tr className="border-b border-slate-200 font-bold text-slate-500 uppercase tracking-wider text-[10px]">
                          {/* CHECKBOX HEADER */}
                          <th className="p-4 pl-6 text-center w-12">
                            <input 
                              type="checkbox" 
                              checked={isAllSelected} 
                              onChange={handleSelectAll} 
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            />
                          </th>
                          <th className="p-4">#</th><th className="p-4">Item Code</th><th className="p-4">Details</th><th className="p-4">Category</th><th className="p-4">Cost Price</th><th className="p-4">Retail Price</th><th className="p-4 text-center">Stock</th><th className="p-4 pr-6 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {processedProducts.length === 0 ? (
                          <tr><td colSpan="9" className="p-8 text-center text-slate-500">No inventory found for selected criteria.</td></tr>
                        ) : (
                          processedProducts.map((p, idx) => (
                            <tr key={p.id} className={`hover:bg-slate-50 transition-colors ${selectedRowIds.includes(p.id) ? "bg-blue-50/50" : ""}`}>
                              {/* CHECKBOX ROW */}
                              <td className="p-4 pl-6 text-center">
                                <input 
                                  type="checkbox" 
                                  checked={selectedRowIds.includes(p.id)} 
                                  onChange={() => handleSelectRow(p.id)} 
                                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                />
                              </td>
                              <td className="p-4 text-slate-500 font-bold">{idx + 1}</td>
                              <td className="p-4 font-mono text-blue-600 font-black">{p.productCode}</td>
                              <td className="p-4 font-bold text-slate-800">{p.productName || p.details}</td>
                              <td className="p-4 font-bold"><span className="bg-slate-100 border border-slate-200 px-2 py-1 rounded text-[9px] uppercase tracking-wider">{p.productType}</span></td>
                              <td className="p-4 font-mono">₹{p.purchasePrice}</td>
                              <td className="p-4 font-mono text-emerald-600 font-black">₹{p.retailPrice}</td>
                              <td className="p-4 font-mono font-black text-center text-slate-800 text-sm">{p.quantity}</td>
                              <td className="p-4 pr-6">
                                <div className="flex gap-2 justify-center">
                                  <button onClick={() => setEditingProduct(p)} className="p-2 bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-600 hover:text-white rounded-lg transition-colors" title="Edit Item">
                                    <Edit3 size={14} />
                                  </button>
                                  <button onClick={() => setBarcodeModal({ open: true, productsToPrint: [p] })} className="p-2 bg-indigo-50 text-indigo-600 border border-indigo-200 hover:bg-indigo-600 hover:text-white rounded-lg transition-colors" title="Print Barcode">
                                    <Printer size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 3: STOCK TRANSFER MULTI-DISPATCH */}
            {activeTab === "transfer" && (
              <div className="space-y-6">
                <div className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
                  
                  {/* HEADER & TOGGLE BUTTON */}
                  <div className="border-b border-slate-100 pb-4 flex justify-between items-center">
                    <h2 className="text-lg font-black uppercase text-slate-800 flex items-center gap-2">
                      <ArrowRightLeft size={20} className="text-emerald-500" /> Multiple Stock Dispatch 
                    </h2>
                    <button 
                      onClick={() => setShowTransferHistory(!showTransferHistory)} 
                      className="px-4 py-2 bg-slate-100 border border-slate-200 text-slate-700 rounded-xl font-bold text-xs hover:bg-slate-200 transition-colors"
                    >
                      {showTransferHistory ? "Hide Transfer History Log" : "Show Transfer History Log"}
                    </button>
                  </div>

                  {/* DESTINATION SELECTION */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-xs font-bold bg-slate-50 p-5 rounded-2xl border border-slate-200">
                    <div className="space-y-1.5">
                      <label className="uppercase tracking-wider text-[10px] text-slate-500">Target Destination Branch *</label>
                      <select value={transferForm.toBranchId} onChange={e => setTransferForm({ ...transferForm, toBranchId: e.target.value })} className="w-full p-3.5 bg-white border rounded-xl outline-none focus:border-blue-500">
                        <option value="">Select Target Store</option>
                        {storesList.filter(s => s.id !== activeBranchId).map(st => <option key={st.id} value={st.id}>{st.storeId || st.id}</option>)}
                      </select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="uppercase tracking-wider text-[10px] text-slate-500">Transfer Remarks / Notes (Optional)</label>
                      <input value={transferForm.remarks} onChange={e => setTransferForm({ ...transferForm, remarks: e.target.value })} placeholder="E.g., Requested by Store Manager..." className="w-full p-3.5 bg-white border rounded-xl outline-none focus:border-blue-500" />
                    </div>
                  </div>

                  {/* TRANSFER STAGING CART */}
                  <div className="border border-emerald-200 rounded-2xl overflow-hidden shadow-sm">
                    <div className="bg-emerald-50 p-4 border-b border-emerald-200 text-emerald-800 font-bold uppercase text-xs tracking-wider flex justify-between items-center">
                      <span>Items Staged for Transfer ({transferCart.length})</span>
                      {transferCart.length > 0 && <span className="bg-emerald-200 text-emerald-900 px-2 py-1 rounded text-[10px]">Review Quantities</span>}
                    </div>
                    {transferCart.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-xs font-bold">
                        No items selected for transfer yet.<br/>Please search and add products from the inventory list below.
                      </div>
                    ) : (
                      <table className="w-full text-left text-xs">
                        <thead className="bg-white border-b text-slate-500">
                          <tr>
                            <th className="p-3">#</th>
                            <th className="p-3">Product Code</th>
                            <th className="p-3">Item Name</th>
                            <th className="p-3 text-center">Available</th>
                            <th className="p-3 text-center">Transfer Qty</th>
                            <th className="p-3 text-center">Remove</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferCart.map((item, idx) => (
                            <tr key={item.product.id} className="border-b bg-white">
                              <td className="p-3 font-bold text-slate-500">{idx + 1}</td>
                              <td className="p-3 font-mono font-black text-blue-600">{item.product.productCode}</td>
                              <td className="p-3 font-bold">{item.product.productName || item.product.details}</td>
                              <td className="p-3 text-center font-bold text-slate-700">{item.product.quantity}</td>
                              <td className="p-3 w-32 mx-auto">
                                <input
                                  type="number"
                                  min="1"
                                  max={item.product.quantity}
                                  value={item.transferQty}
                                  onChange={(e) => {
                                      let val = Number(e.target.value);
                                      if (val > item.product.quantity) val = item.product.quantity;
                                      if (val < 1) val = 1;
                                      const newCart = [...transferCart];
                                      newCart[idx].transferQty = val;
                                      setTransferCart(newCart);
                                  }}
                                  className="w-full p-2 border border-slate-300 rounded-lg text-center font-bold outline-none focus:border-blue-500"
                                />
                              </td>
                              <td className="p-3 text-center">
                                <button onClick={() => setTransferCart(transferCart.filter(c => c.product.id !== item.product.id))} className="text-rose-500 hover:bg-rose-50 p-2 rounded-lg transition-colors"><Trash2 size={16}/></button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    <div className="bg-slate-50 p-4 border-t border-slate-200 flex justify-end">
                      <button onClick={executeStockTransfer} disabled={transferCart.length === 0} className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase tracking-widest rounded-xl text-xs flex items-center gap-2 shadow-md shadow-emerald-200 transition-colors">
                        <Truck size={16}/> Execute Bulk Dispatch
                      </button>
                    </div>
                  </div>

                  {/* INVENTORY SEARCH LIST FOR TRANSFER */}
                  <div className="border border-slate-200 rounded-2xl overflow-hidden mt-8 shadow-sm">
                    <div className="bg-slate-50 p-4 border-b border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
                      <h3 className="text-xs font-black text-slate-700 uppercase tracking-wider">Select Inventory to Transfer</h3>
                      <div className="relative w-full md:w-80">
                        <Search className="absolute left-3 top-3 text-slate-400" size={14} />
                        <input
                          type="text"
                          placeholder="Search via Brand, Code, or Name..."
                          value={transferSearch}
                          onChange={(e) => setTransferSearch(e.target.value)}
                          className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 text-xs font-bold rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>
                    
                    <div className="max-h-[400px] overflow-y-auto">
                      <table className="w-full text-left text-xs">
                        <thead className="bg-white border-b text-slate-500 sticky top-0 shadow-sm z-10">
                          <tr>
                            <th className="p-4">#</th>
                            <th className="p-4">Code</th>
                            <th className="p-4">Product Name</th>
                            <th className="p-4">Brand</th>
                            <th className="p-4 text-center">Stock Available</th>
                            <th className="p-4 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {products.filter(p => {
                             const q = transferSearch.toLowerCase();
                             return ((p.productCode || "").toLowerCase().includes(q) ||
                                     (p.productName || p.details || "").toLowerCase().includes(q) ||
                                     (p.brand || "").toLowerCase().includes(q));
                          }).map((p, idx) => {
                              const inCart = transferCart.find(c => c.product.id === p.id);
                              return (
                                  <tr key={p.id} className="hover:bg-slate-50">
                                      <td className="p-4 font-bold text-slate-400">{idx + 1}</td>
                                      <td className="p-4 font-mono font-bold text-blue-600">{p.productCode}</td>
                                      <td className="p-4 font-bold">{p.productName || p.details}</td>
                                      <td className="p-4">{p.brand || '-'}</td>
                                      <td className="p-4 text-center font-bold text-emerald-600">{p.quantity}</td>
                                      <td className="p-4 text-center">
                                          {inCart ? (
                                              <span className="text-emerald-600 font-bold text-[10px] uppercase bg-emerald-50 px-3 py-2 rounded-lg border border-emerald-200"><Check size={12} className="inline mr-1"/> Added</span>
                                          ) : (
                                              <button
                                                  onClick={() => p.quantity > 0 ? setTransferCart([...transferCart, { product: p, transferQty: 1 }]) : alert('Item is Out of Stock!')}
                                                  className="text-[10px] font-bold uppercase bg-blue-50 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-600 hover:text-white transition-colors"
                                              >
                                                  + Add to Transfer
                                              </button>
                                          )}
                                      </td>
                                  </tr>
                              );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* TRANSFER HISTORY LOG VIEW IN TRANSFER SECTION (TOGGLABLE) */}
                <AnimatePresence>
                {showTransferHistory && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="max-w-6xl mx-auto bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mt-6">
                    <h3 className="text-sm font-black uppercase text-slate-800 flex items-center gap-2 border-b border-slate-100 pb-4 mb-4">
                      <History size={16} className="text-blue-500"/> Stock Transfer History Log
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse text-xs">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                          <tr>
                            <th className="p-4">#</th>
                            <th className="p-4">Date</th>
                            <th className="p-4">Product Code</th>
                            <th className="p-4">Item Name</th>
                            <th className="p-4">From Branch</th>
                            <th className="p-4">To Branch</th>
                            <th className="p-4 text-center">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transferLogs?.length === 0 ? (
                            <tr><td colSpan="7" className="p-4 text-center text-slate-500">No transfer history recorded.</td></tr>
                          ) : (
                            transferLogs?.map((log, idx) => (
                              <tr key={log.id} className="border-b hover:bg-slate-50">
                                <td className="p-4 font-bold text-slate-400">{idx + 1}</td>
                                <td className="p-4">{log.transferredAt ? new Date(log.transferredAt).toLocaleString() : 'N/A'}</td>
                                <td className="p-4 font-mono font-bold text-blue-600">{log.productCode || 'N/A'}</td>
                                <td className="p-4 font-bold">{log.productName || 'N/A'}</td>
                                <td className="p-4">{log.fromBranch || 'N/A'}</td>
                                <td className="p-4 font-bold">{log.toBranch || 'N/A'}</td>
                                <td className="p-4 font-bold text-emerald-600 text-center">{log.quantity || 0}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </motion.div>
                )}
                </AnimatePresence>
              </div>
            )}

            {/* TAB 4: EDIT LOGS */}
            {activeTab === "editLogs" && (
              <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 space-y-6 shadow-sm">
                <div className="border-b border-slate-100 pb-4">
                  <h2 className="text-lg font-black uppercase text-slate-800 flex items-center gap-2"><ClipboardEdit size={20} className="text-blue-500" /> Stock Audit Edit Logs</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                      <tr><th className="p-4">#</th><th className="p-4">Timestamp</th><th className="p-4">Product Code</th><th className="p-4">Item Name</th><th className="p-4">Qty Change</th><th className="p-4">Price Change</th></tr>
                    </thead>
                    <tbody>
                      {editLogs.length === 0 ? <tr><td colSpan="6" className="p-4 text-center">No edits logged.</td></tr> :
                        editLogs.map((log, idx) => (
                          <tr key={log.id} className="border-b">
                            <td className="p-4 font-bold text-slate-400">{idx + 1}</td>
                            <td className="p-4 font-bold">{new Date(log.timestamp).toLocaleString()}</td>
                            <td className="p-4 text-blue-600 font-mono font-black">{log.productCode}</td>
                            <td className="p-4 font-bold">{log.productName}</td>
                            <td className="p-4">{log.oldQuantity} → <strong className="text-emerald-600">{log.newQuantity}</strong></td>
                            <td className="p-4">₹{log.oldPrice} → <strong className="text-emerald-600">₹{log.newPrice}</strong></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* TAB 5: COMBINED REPORTS */}
            {activeTab === "analyticsDashboard" && (
              <div className="space-y-8">
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm">
                  <h2 className="text-lg font-black uppercase text-slate-800 border-b border-slate-100 pb-4 mb-4">Master Reports Console</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 font-bold text-sm p-4 text-slate-700">Inventory Status Report</div>
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-left text-xs"><thead className="sticky top-0 bg-white shadow-sm"><tr><th className="p-3">#</th><th className="p-3">Code</th><th className="p-3">Stock</th><th className="p-3">Cost Value</th></tr></thead><tbody>{products.map((p, idx) => <tr key={p.id} className="border-b"> <td className="p-3">{idx + 1}</td><td className="p-3 font-mono">{p.productCode}</td><td className="p-3">{p.quantity}</td><td className="p-3">₹{(p.purchasePrice * p.quantity).toLocaleString()}</td></tr>)}</tbody></table>
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 font-bold text-sm p-4 text-slate-700">Purchase Activity Report</div>
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-left text-xs"><thead className="sticky top-0 bg-white shadow-sm"><tr><th className="p-3">#</th><th className="p-3">Date</th><th className="p-3">Supplier</th><th className="p-3">Total Amount</th></tr></thead><tbody>{purchaseHistory.map((ph, idx) => <tr key={ph.id} className="border-b"> <td className="p-3">{idx + 1}</td><td className="p-3">{new Date(ph.createdAt).toLocaleDateString()}</td><td className="p-3">{ph.supplier}</td><td className="p-3 font-bold text-emerald-600">₹{ph.totalAmount}</td></tr>)}</tbody></table>
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 font-bold text-sm p-4 text-slate-700">Stock Transfer Report</div>
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-left text-xs"><thead className="sticky top-0 bg-white shadow-sm"><tr><th className="p-3">#</th><th className="p-3">Date</th><th className="p-3">Item</th><th className="p-3">To Branch</th><th className="p-3">Qty</th></tr></thead><tbody>{transferLogs.map((tl, idx) => <tr key={tl.id} className="border-b"> <td className="p-3">{idx + 1}</td><td className="p-3">{new Date(tl.transferredAt).toLocaleDateString()}</td><td className="p-3">{tl.productCode}</td><td className="p-3">{tl.toBranch}</td><td className="p-3 font-bold">{tl.quantity}</td></tr>)}</tbody></table>
                      </div>
                    </div>
                    <div className="border border-slate-200 rounded-xl overflow-hidden">
                      <div className="bg-slate-50 font-bold text-sm p-4 text-slate-700">Modification Log Report</div>
                      <div className="max-h-[300px] overflow-y-auto">
                        <table className="w-full text-left text-xs"><thead className="sticky top-0 bg-white shadow-sm"><tr><th className="p-3">#</th><th className="p-3">Date</th><th className="p-3">Item</th><th className="p-3">New Qty</th></tr></thead><tbody>{editLogs.map((log, idx) => <tr key={log.id} className="border-b"> <td className="p-3">{idx + 1}</td><td className="p-3">{new Date(log.timestamp).toLocaleDateString()}</td><td className="p-3">{log.productCode}</td><td className="p-3 font-bold text-blue-600">{log.newQuantity}</td></tr>)}</tbody></table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 6: GLOBAL WAREHOUSE / INVENTORY */}
            {activeTab === "globalInventory" && (
              <div className="space-y-6">
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm min-h-[60vh] flex flex-col items-center">
                  
                  {/* CENTERED SEARCH BAR */}
                  <div className="w-full max-w-2xl mx-auto mb-8 text-center mt-8">
                    <h2 className="text-2xl font-black uppercase text-slate-800 mb-6 flex justify-center items-center gap-3">
                      <Globe size={28} className="text-blue-500" /> Global Warehouse Search
                    </h2>
                    <div className="relative w-full">
                      <Search className="absolute left-4 top-4 text-slate-400" size={20} />
                      <input
                        type="text"
                        placeholder="Enter Product Code or Name to search globally..."
                        value={globalSearch}
                        onChange={(e) => setGlobalSearch(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border-2 border-slate-200 text-base font-bold rounded-2xl outline-none focus:bg-white focus:border-blue-500 transition-colors shadow-sm"
                      />
                    </div>
                  </div>

                  {/* CONDITIONALLY RENDERED RESULTS */}
                  <div className="w-full">
                    {isProcessing ? (
                      <div className="p-12 text-center text-slate-500 font-bold text-sm">
                        <RefreshCw className="animate-spin mx-auto mb-2 text-blue-500" size={24} />
                        Fetching Global Inventory Data...
                      </div>
                    ) : !globalSearch.trim() ? (
                      <div className="p-16 text-center text-slate-400 flex flex-col items-center justify-center border-2 border-dashed border-slate-100 rounded-2xl bg-slate-50/50">
                        <Search size={48} className="mb-4 text-slate-300 opacity-50"/>
                        <p className="font-bold text-lg text-slate-500">Search for a product</p>
                        <p className="text-xs mt-1 max-w-sm">Enter a product name or code above to locate inventory across all branches and the Super Admin Warehouse.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto w-full">
                        <table className="w-full text-left border-collapse text-xs">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                            <tr>
                              <th className="p-4">#</th>
                              <th className="p-4">Store / Branch</th>
                              <th className="p-4">Product Code</th>
                              <th className="p-4">Product Name / Details</th>
                              <th className="p-4">Category</th>
                              <th className="p-4">Available Quantity</th>
                            </tr>
                          </thead>
                          <tbody>
                            {globalInventory
                              .filter(item => {
                                const q = globalSearch.toLowerCase();
                                return (
                                  (item.productCode || "").toLowerCase().includes(q) ||
                                  (item.productName || item.details || "").toLowerCase().includes(q)
                                );
                              })
                              .map((item, idx) => (
                                <tr key={`${item.storeId}-${item.id}-${idx}`} className="border-b hover:bg-slate-50 transition-colors">
                                  <td className="p-4 font-bold text-slate-400">{idx + 1}</td>
                                  <td className="p-4 font-bold text-indigo-600">{item.storeId}</td>
                                  <td className="p-4 font-mono font-black text-blue-600">{item.productCode}</td>
                                  <td className="p-4 font-bold text-slate-800">{item.productName || item.details}</td>
                                  <td className="p-4">
                                    <span className="bg-slate-100 border border-slate-200 px-2 py-1 rounded text-[9px] uppercase tracking-wider">
                                      {item.productType || 'N/A'}
                                    </span>
                                  </td>
                                  <td className="p-4 font-mono font-black text-emerald-600 text-sm">{item.quantity}</td>
                                </tr>
                              ))}
                            {globalInventory.filter(item => {
                                const q = globalSearch.toLowerCase();
                                return (
                                  (item.productCode || "").toLowerCase().includes(q) ||
                                  (item.productName || item.details || "").toLowerCase().includes(q)
                                );
                              }).length === 0 && (
                              <tr><td colSpan="6" className="p-8 text-center text-slate-500 font-bold">No matching inventory found across branches.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              </div>
            )}
            
          </motion.div>
        </AnimatePresence>

        {/* --- GLOBAL MODALS --- */}
        <ProductEntryModal 
          isOpen={productModal.open}
          type={productModal.type}
          isDirectInventory={productModal.isDirectInventory}
          onClose={() => setProductModal({ open: false, type: "", isDirectInventory: false })}
          onAdd={productModal.isDirectInventory ? handleAddDirectInventory : handleAddInvoiceItem}
        />

        {/* --- UPDATED BULK BARCODE MODAL --- */}
        <BarcodePrintModal 
          isOpen={barcodeModal.open}
          productsToPrint={barcodeModal.productsToPrint}
          storeName={storeName}
          onClose={() => setBarcodeModal({ open: false, productsToPrint: [] })}
        />

        {/* --- ADD NEW VENDOR / SUPPLIER MODAL --- */}
        {isVendorModalOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white p-6 rounded-3xl w-full max-w-sm shadow-2xl">
              <h3 className="text-lg font-black uppercase text-slate-800 mb-4 flex justify-between items-center border-b pb-3 border-slate-100">
                <span className="flex items-center gap-2"><UserPlus size={18} className="text-blue-600" /> Add Vendor</span>
                <button onClick={() => setIsVendorModalOpen(false)} className="text-slate-400 hover:text-rose-500 transition-colors"><X size={20}/></button>
              </h3>
              
              <div className="space-y-4 text-xs font-bold text-slate-600">
                <div>
                  <label className="block mb-1.5 uppercase tracking-wider text-[10px]">Vendor Name *</label>
                  <input value={vendorForm.name} onChange={e => setVendorForm({...vendorForm, name: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition-colors"/>
                </div>
                <div>
                  <label className="block mb-1.5 uppercase tracking-wider text-[10px]">Contact No</label>
                  <input value={vendorForm.contactNo} onChange={e => setVendorForm({...vendorForm, contactNo: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition-colors"/>
                </div>
                <div>
                  <label className="block mb-1.5 uppercase tracking-wider text-[10px]">GSTIN</label>
                  <input value={vendorForm.gstin} onChange={e => setVendorForm({...vendorForm, gstin: e.target.value})} className="w-full p-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition-colors uppercase font-mono"/>
                </div>
                <div>
                  <label className="block mb-1.5 uppercase tracking-wider text-[10px]">Address</label>
                  <textarea value={vendorForm.address} onChange={e => setVendorForm({...vendorForm, address: e.target.value})} rows={2} className="w-full p-2.5 border border-slate-200 rounded-xl bg-slate-50 focus:bg-white focus:border-blue-500 outline-none transition-colors resize-none"/>
                </div>
              </div>

              <div className="flex gap-3 mt-6 border-t border-slate-100 pt-4">
                <button onClick={() => setIsVendorModalOpen(false)} className="flex-1 py-3 bg-slate-100 rounded-xl text-slate-600 font-bold hover:bg-slate-200 transition-colors">Cancel</button>
                <button onClick={submitVendor} disabled={isProcessing} className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-black hover:bg-blue-700 shadow-md shadow-blue-200 transition-colors disabled:opacity-50">Save Vendor</button>
              </div>
            </motion.div>
          </div>
        )}

        {/* --- FULL DATA EDIT INVENTORY MODAL --- */}
        {editingProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 overflow-y-auto">
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white p-6 rounded-3xl w-full max-w-4xl shadow-2xl my-8">
              <h3 className="text-lg font-black uppercase text-slate-800 mb-4 flex justify-between items-center border-b pb-3">
                <span>Edit {editingProduct.productType} Inventory Data</span>
                <button onClick={() => setEditingProduct(null)} className="text-slate-400 hover:text-rose-500"><X size={20}/></button>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-bold overflow-y-auto max-h-[60vh] pr-2">
                {Object.keys(editingProduct).map(key => {
                  const hiddenKeys = ['id', 'createdAt', 'updatedAt', 'productType', 'supplier', 'billNo', 'purchaseDate'];
                  if (hiddenKeys.includes(key)) return null;
                  
                  return (
                    <div key={key} className="flex flex-col">
                      <label className="text-slate-500 capitalize mb-1">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                      <input 
                        name={key}
                        type={['quantity', 'purchasePrice', 'retailPrice', 'purchaseRs', 'purchaseRsPerBox'].includes(key) ? "number" : "text"}
                        value={editingProduct[key] || ""} 
                        onChange={handleEditChange} 
                        className="p-2 border border-slate-200 bg-slate-50 rounded focus:bg-white focus:border-blue-500" 
                      />
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3 mt-6 border-t pt-4">
                <button onClick={() => setEditingProduct(null)} className="flex-1 py-3 bg-slate-100 rounded-xl text-slate-600 font-bold hover:bg-slate-200">Cancel</button>
                <button onClick={() => handleEditInventorySave(editingProduct)} className="flex-1 py-3 bg-blue-600 rounded-xl text-white font-black uppercase hover:bg-blue-700 shadow shadow-blue-200">Save All Changes</button>
              </div>
            </motion.div>
          </div>
        )}

      </div>
    </div>
  );
}